import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, ChevronDown, RefreshCw } from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear } from 'date-fns';
import { cn } from '@/lib/utils';

const PRESET_RANGES = [
  { label: 'Last 7 Days', value: '7d', getDates: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Last 30 Days', value: '30d', getDates: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: 'Last 90 Days', value: '90d', getDates: () => ({ start: subDays(new Date(), 90), end: new Date() }) },
  { label: 'This Month', value: 'month', getDates: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  { label: 'Last Month', value: 'last_month', getDates: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'This Quarter', value: 'quarter', getDates: () => ({ start: startOfQuarter(new Date()), end: new Date() }) },
  { label: 'Year to Date', value: 'ytd', getDates: () => ({ start: startOfYear(new Date()), end: new Date() }) },
  { label: 'Custom Range', value: 'custom', getDates: () => null }
];

export function ReportTimeframeSelector({ 
  onTimeframeChange, 
  onRefresh,
  loading = false,
  className,
  showRefresh = true,
  defaultValue = '30d'
}) {
  const [selectedPreset, setSelectedPreset] = useState(defaultValue);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    
    if (value === 'custom') {
      setIsCustomOpen(true);
      return;
    }
    
    const preset = PRESET_RANGES.find(p => p.value === value);
    if (preset) {
      const dates = preset.getDates();
      if (dates) {
        setStartDate(dates.start);
        setEndDate(dates.end);
        onTimeframeChange?.({
          preset: value,
          startDate: format(dates.start, 'yyyy-MM-dd'),
          endDate: format(dates.end, 'yyyy-MM-dd')
        });
      }
    }
  };

  const handleCustomApply = () => {
    setIsCustomOpen(false);
    onTimeframeChange?.({
      preset: 'custom',
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    });
  };

  const getDisplayLabel = () => {
    if (selectedPreset === 'custom') {
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    return PRESET_RANGES.find(p => p.value === selectedPreset)?.label || 'Select Range';
  };

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)} data-testid="report-timeframe-selector">
      {/* Preset Selector */}
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px]" data-testid="timeframe-preset-select">
          <SelectValue placeholder="Select timeframe" />
        </SelectTrigger>
        <SelectContent>
          {PRESET_RANGES.map(preset => (
            <SelectItem key={preset.value} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom Date Range Popover */}
      <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className={cn(
              "justify-start text-left font-normal min-w-[280px]",
              selectedPreset === 'custom' && "ring-2 ring-indigo-500"
            )}
            data-testid="custom-date-range-btn"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {getDisplayLabel()}
            <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium mb-2">Start Date</p>
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => date && setStartDate(date)}
                  disabled={(date) => date > endDate || date > new Date()}
                  initialFocus
                />
              </div>
              <div>
                <p className="text-sm font-medium mb-2">End Date</p>
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => date && setEndDate(date)}
                  disabled={(date) => date < startDate || date > new Date()}
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <Badge variant="outline" className="text-xs">
                {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} days selected
              </Badge>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsCustomOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCustomApply} data-testid="apply-custom-range-btn">
                  Apply Range
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Refresh Button */}
      {showRefresh && (
        <Button 
          variant="outline" 
          size="icon"
          onClick={onRefresh}
          disabled={loading}
          data-testid="refresh-report-btn"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      )}
    </div>
  );
}

export default ReportTimeframeSelector;
